/* eslint-disable @nx/enforce-module-boundaries */
import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Pagination, RichText, Messages, useCheckBreakpoint } from '@marriott/mi-ui-library';
import { AHeader, AHeaderSize, AHeaderTag, AIcon, AIconSize, ASubheader } from '@marriott/mi-web-ui-kit';
import { useClientEnvVarsStore, useWishListDetailsGlobalStore, WishListItemStoreType } from '@marriott/mi-store-utils';
import { constructAdfOrRlmURL } from '@marriott/mi-shop-components/src/organisms';
import { useWishlistModalStore } from '../../modules/store/wishlistModalStore';
import { WISHLIST_MODAL_TYPE } from '../../modules';
import {
  generateApolloClientHeaders,
  generateViewHotelUrl,
  generateRitzCarltonHotelUrl,
  getImageDomain,
  getFallbackImage,
  ValidMarshCodes,
  ValidBrandIds,
  ritzCarltonBrandId,
  WISHLIST_DETAIL_PROPERTY_PAGE_SIZE_TABMOBILE,
  WISHLIST_DETAIL_PROPERTY_PAGE_SIZE_LAPTOP,
} from '../../modules/utils';
import { usePageContext } from '../../modules/context/PageContext';
import { phoenixAccountGetPropertyRatesByPropertyID, phoenixAccountUpdateCustomerWishlist } from '../../modules/graph';
import { FindFavorites } from '../SavedHotelsDetails/FindFavorites';
import { constants } from '../../modules/utils/constants/constants';
import { createWishlistsPayload, getTodayAndTomorrowDates } from '../../modules/utils/wishlistHelper';
import { replaceText } from '../../modules/utils/promotionHelper';
import { WishListDetailViewStyle } from './WishListDetailView.style';
import { WishlistDetailViewHeader } from './WishlistDetailView.header';
import {
  WishListDetailViewProps,
  WishListRateNode,
  WishListRatesResponse,
  WishListMutationResponse,
} from './WishlistDetailView.types';
import wishlistHotelRatesMock from './__mock__/WishlistDetailView.mock.json';
import wishlistGlobalStoreMock from './__mock__/WishlistDetailGlobalStoreResponse.json';
import { useSession } from '@marriott/mi-headless-utils';

const PropertyCard = dynamic(() => import('@marriott/mi-shop-components/molecules').then(mod => mod.PropertyCard));

export const WishListDetailView: React.FC<WishListDetailViewProps> = ({ isAuthorMode, authoredProp }) => {
  const isLocalDev = process.env['IS_LOCAL_DEV'] === 'true';
  const pageContext = usePageContext();
  const authoredProps = authoredProp;
  const { envVarsObject } = useClientEnvVarsStore();
  const currentLocale = envVarsObject?.['ACCEPT_LANGUAGE'] ?? '';
  const { session } = useSession();
  const sessionData = session?.data;
  const imageBaseUrl = getImageDomain(currentLocale);
  const isLaptopViewPortAbove = useCheckBreakpoint('viewportL');
  const pageSize = isLaptopViewPortAbove
    ? WISHLIST_DETAIL_PROPERTY_PAGE_SIZE_LAPTOP
    : WISHLIST_DETAIL_PROPERTY_PAGE_SIZE_TABMOBILE;
  const { setModalType, setWishlistId, wishlistId } = useWishlistModalStore(state => state);
  const { wishlist, setWishListDetailsInfo } = useWishListDetailsGlobalStore(state => state);
  const updatedWishlistsRef = useRef<WishListItemStoreType[]>([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [ratesData, setRatesData] = useState<WishListRatesResponse | null>(null);
  const [ratesError, setRatesError] = useState(false);
  const [removePropertyError, setRemovePropertyError] = useState(false);

  const currentWishlist = wishlist?.find(item => item.wishlistId === wishlistId) ?? null;

  const properties = isAuthorMode
    ? wishlistGlobalStoreMock.data.customerWishlist.wishlists[0].properties
        .map(prop => ({
          propertyId: prop.id ?? '',
          name: prop.basicInformation?.nameInDefaultLanguage ?? '',
          brandId: prop.basicInformation?.brand?.id ?? '',
          brandName: prop.basicInformation?.brand?.name ?? '',
          images: (prop.media?.photoGallery?.features?.edges ?? []).map(edge => ({
            url: edge?.node?.imageUrls?.square ?? '',
            wideHorizontalUrl: edge?.node?.imageUrls?.wideHorizontal ?? '',
          })),
          reviews: {
            starsCount: prop.reviews?.stars?.count,
            numberOfReviewsCount: prop.reviews?.numberOfReviews?.count,
          },
        }))
        .filter(property => property.propertyId)
    : currentWishlist?.properties ?? [];

  const totalCount = properties?.length;
  const allPropertyIds = properties?.map(property => property?.propertyId).filter((id): id is string => !!id);
  const pageStart = (currentPage - 1) * pageSize;
  const pagePropertyIds = allPropertyIds.slice(pageStart, pageStart + pageSize);
  const paginatedProperties = properties.slice(pageStart, pageStart + pageSize);
  const pageDisplayStart = totalCount > 0 ? pageStart + 1 : 0;
  const pageDisplayEnd = pageStart + paginatedProperties.length;
  const numberOfPropertiesLabelTemplate = authoredProps?.numberOfPropertiesLabel || '';
  const numberOfPropertiesLabel = replaceText(numberOfPropertiesLabelTemplate, [
    String(pageDisplayStart),
    String(pageDisplayEnd),
    String(totalCount),
  ]);
  const pagePropertyIdsKey = pagePropertyIds.join('|');

  const [fetchRates, { loading: ratesLoading }] = useLazyQuery(phoenixAccountGetPropertyRatesByPropertyID, {
    fetchPolicy: 'no-cache',
    context: generateApolloClientHeaders(isLocalDev, pageContext),
    onCompleted: data => {
      setRatesData(data as WishListRatesResponse);
      setRatesError(false);
    },
    onError: () => {
      setRatesError(true);
    },
  });

  const [updateCustomerWishlist, { loading: isRemovingProperty }] = useMutation<WishListMutationResponse>(
    phoenixAccountUpdateCustomerWishlist,
    {
      onCompleted: data => {
        const statusArr = data?.createCustomerWishlist?.status;
        const isSuccess =
          Array.isArray(statusArr) && statusArr?.some(status => status.code === constants.STATUS_SUCCESS);
        if (isSuccess) {
          setRemovePropertyError(false);
          setWishListDetailsInfo({ wishlist: updatedWishlistsRef.current });
        } else {
          setRemovePropertyError(true);
        }
      },
      onError: () => {
        setRemovePropertyError(true);
      },
    }
  );

  useEffect(() => {
    if ((!isAuthorMode && !wishlistId) || pagePropertyIds?.length === 0) return;
    if (isAuthorMode) {
      setRatesData(
        ((wishlistHotelRatesMock as { data?: WishListRatesResponse })?.data ??
          wishlistHotelRatesMock) as WishListRatesResponse
      );
      return;
    }

    const dates = getTodayAndTomorrowDates();

    const startDate = currentWishlist?.startDate?.slice(0, 10) || dates?.todayDate;
    const endDate = currentWishlist?.endDate?.slice(0, 10) || dates?.tomorrowDate;
    fetchRates({
      variables: {
        search: {
          ids: pagePropertyIds,
          options: { startDate, endDate, rateRequestTypes: [{ type: 'STANDARD', value: '' }], customerId: '' },
        },
        limit: pageSize,
        offset: null,
      },
    });
  }, [
    currentWishlist?.endDate,
    currentWishlist?.startDate,
    fetchRates,
    isAuthorMode,
    pagePropertyIdsKey,
    pageSize,
    wishlistId,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [wishlistId, pageSize]);

  const handleRemoveProperty = async (propertyId: string) => {
    if (!wishlistId || isRemovingProperty) return;

    const updatedWishlists = (wishlist || [])?.map(item => {
      if (item.wishlistId !== wishlistId) return item;
      const updatedProperties = (item?.properties || [])?.filter(
        property => property?.propertyId?.toLowerCase() !== propertyId?.toLowerCase()
      );
      return { ...item, properties: updatedProperties, wishlistSavedCount: updatedProperties?.length };
    });
    updatedWishlistsRef.current = updatedWishlists;
    setRemovePropertyError(false);

    try {
      await updateCustomerWishlist({
        variables: {
          input: { customerId: sessionData?.consumerID, wishlists: createWishlistsPayload(updatedWishlists) },
        },
        context: generateApolloClientHeaders(isLocalDev, pageContext),
      });
    } catch {
      setRemovePropertyError(true);
    }
  };

  const emptyStateModel = {
    title: authoredProps?.detailsEmptyStateHeader || '',
    description: authoredProps?.detailsEmptyStateDescription || '',
    ctaLabel: authoredProps?.startBrowsingCta || '',
    ctaPath: authoredProps?.emptystateLink,
    ctaPathOthers: authoredProps?.emptystateLink,
  };
  const handleRename = () => {
    setModalType(WISHLIST_MODAL_TYPE.RENAME);
  };

  const handleDelete = () => {
    setModalType(WISHLIST_MODAL_TYPE.DELETE);
  };

  return (
    <WishListDetailViewStyle className="wishlist-detail-view" data-testid="wishlist-detail-view">
      <Button
        className="wishlist-detail-backtolanding m-link-tertiary-button t-font-s mb-4 ml-4 align-items-center"
        callback={() => setWishlistId(null)}
        testId="wishlist-detail-back"
      >
        <span>{authoredProps?.backToWishlistBtn}</span>
      </Button>

      <div className="d-flex align-items-center mb-2">
        <AHeader
          text={currentWishlist?.wishlistName || ''}
          size={AHeaderSize.TitleM}
          headerTag={AHeaderTag.h2}
          className="mb-0 mr-2"
        />
        <Button className="ml-2" ariaLabel="Rename wishlist" callback={handleRename} testId="wishlist-detail-rename">
          <AIcon icon="icon-edit" size={AIconSize.S} />
        </Button>
      </div>

      <div className="wishlist-detail-view__dropdown_section d-flex align-items-center flex-wrap mb-4">
        <WishlistDetailViewHeader
          authoredProps={authoredProps}
          currentLocale={currentLocale}
          currentWishlist={currentWishlist}
          envVarsObject={envVarsObject}
          fetchRates={fetchRates}
          isAuthorMode={isAuthorMode}
          isLocalDev={isLocalDev}
          pageContext={pageContext}
          pagePropertyIds={pagePropertyIds}
          pageSize={pageSize}
          sessionData={sessionData}
          setWishListDetailsInfo={setWishListDetailsInfo}
          wishlist={wishlist}
          wishlistId={wishlistId}
          ratesError={ratesError}
        />

        <Button className="ml-auto" ariaLabel="Delete wishlist" callback={handleDelete} testId="wishlist-detail-delete">
          <AIcon icon="icon-delete" size={AIconSize.S} />
        </Button>
      </div>

      <hr className="wishlist-detail-view__divider" />

      {totalCount > pageSize && <div className="mb-3 t-font-m">{numberOfPropertiesLabel}</div>}

      {removePropertyError && (
        <div className="mt-2 mb-4 mx-2">
          <div data-testid="uxl-error-msg" className="uxl-error-msg">
            <Messages messageType="warning" className="mb-4">
              <RichText text={authoredProps?.wishlistDetailErrorMessage || ''} componentId="uxl-error-msg" />
            </Messages>
          </div>
        </div>
      )}

      {totalCount === 0 && (
        <div className="pt-4">
          <ASubheader text={authoredProps?.emptyStateSubHeader || ''} />
          <FindFavorites
            model={emptyStateModel}
            iconClass="icon-heart-outline icon-decorative"
            isVariation2={true}
            secondaryTypeBtn={false}
            customClass="find-favourite-card p-0"
          />
        </div>
      )}

      {totalCount > 0 && ratesLoading && (
        <div className="row">
          {Array.from({ length: pageSize }).map((_, index) => (
            <div className="col-12  col-md-6 col-lg-4 pb-3 pb-md-4" key={index}>
              <div className="wishlist-landing__skeleton-card">
                <div className="wishlist-landing__skeleton-image"></div>
                <div className="p-3">
                  <div className="wishlist-landing__skeleton-title"></div>
                  <div className="wishlist-landing__skeleton-text"></div>
                  <div className="wishlist-landing__skeleton-text wishlist-landing__skeleton-text--short"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalCount > 0 && !ratesLoading && (
        <div className="row">
          {paginatedProperties.map(property => {
            const propertyId = property.propertyId ?? '';
            const rateNode = ratesData?.search?.lowestAvailableRates?.searchByPropertyIds?.edges?.find(
              edge => edge?.node?.property?.id?.toLowerCase() === propertyId?.toLowerCase()
            )?.node as WishListRateNode | undefined;
            // Use store data for property details; fall back to rate query data if not available
            const propertyName = property.name ?? '';
            const brandId = property.brandId ?? '';
            const brandName = property.brandName ?? '';
            const isExternal = ValidMarshCodes?.includes(propertyId) || ValidBrandIds?.includes(brandId);
            const isRitzCarlton = brandId?.toLowerCase() === ritzCarltonBrandId;
            const hotelUrl = isRitzCarlton
              ? generateRitzCarltonHotelUrl(propertyId, propertyName, currentLocale)
              : generateViewHotelUrl(propertyId, propertyName);
            // Use store images (wideHorizontalUrl preferred, then url), fall back to rate query primary images
            const storeImages = (property.images ?? [])
              .map(image => image?.wideHorizontalUrl ?? image?.url)
              .filter((url): url is string => !!url)
              .slice(0, 5);
            const imageSource = storeImages;
            const images = imageSource.length
              ? imageSource.map(url => ({ defaultImageUrl: imageBaseUrl + url, altText: propertyName || '' }))
              : [{ defaultImageUrl: getFallbackImage(currentLocale)?.Classic }];
            // Use store reviews; fall back to rate query reviews
            const starsCount = property.reviews?.starsCount;
            const reviewCount = property.reviews?.numberOfReviewsCount;
            const formattedReviewCount = reviewCount ? Number(reviewCount).toLocaleString(currentLocale) : '';
            const reviewsDetails =
              starsCount && reviewCount
                ? {
                    reviewsText: authoredProps?.reviews?.replace('{0}', formattedReviewCount) || '',
                    reviewsAvg: String(starsCount),
                    reviewsLink: '#',
                    reviewsLabel: authoredProps?.reviews
                      ? `${formattedReviewCount} ${authoredProps.reviews}`
                      : formattedReviewCount,
                  }
                : undefined;
            const lowestRate = rateNode?.rates?.[0]?.rateModes?.lowestAverageRate;
            const rawAmount = lowestRate?.amount?.amount;
            const decimalPoint = lowestRate?.amount?.decimalPoint ?? 0;
            const priceCurrency = lowestRate?.amount?.currency;
            const formattedPrice =
              rawAmount != null
                ? (() => {
                    const actual = rawAmount / Math.pow(10, decimalPoint);
                    return actual % 1 === 0 ? String(Math.round(actual)) : actual.toFixed(decimalPoint);
                  })()
                : undefined;
            const ctaURL = constructAdfOrRlmURL(
              authoredProps?.bookNowCtaPath || '',
              '?isRateCalendar=true&propertyCode=',
              '?propertyCode=',
              !(!!currentWishlist?.startDate && !!currentWishlist?.endDate),
              propertyId,
              priceCurrency || '',
              false
            );
            const titleDetails = {
              title: propertyName ?? '',
              isTitleLink: false,
              titleClassNames: ['m-ellipsis-2lines px-3'],
            };
            const brandDetails = brandId ? { brandId, label: brandName } : undefined;
            const tertiaryLinkDetails = {
              isLink: true,
              href: hotelUrl,
              className: 'm-link-tertiary-button py-0 my-3',
              buttonCopy: authoredProps?.viewHotelLabel || '',
              target: isExternal ? '_blank' : '_self',
              placeLinkOnTopSection: false,
            };
            const footerLinkDetails = {
              href: ctaURL,
              text: authoredProps?.viewRateBtn,
              className: 'm-button-s mt-1 m-button-primary',
              ...(formattedPrice && priceCurrency
                ? {
                    hasPrice: true,
                    priceValue: formattedPrice,
                    priceType: authoredProps?.ratesLabel?.replace('{0}', priceCurrency),
                    priceAriaLabel: `${formattedPrice} ${priceCurrency}`,
                    isPointSaver: false,
                  }
                : {}),
            };

            const isPropertyInWishlist = !!currentWishlist?.properties?.some(
              wishlistProperty => wishlistProperty?.propertyId?.toLowerCase() === propertyId?.toLowerCase()
            );

            return (
              <div className="col-12 col-md-6 col-lg-4 pb-3 pb-md-4" key={propertyId || property.propertyId}>
                <div className="wishlist-detail-view__card-wrapper">
                  <Button
                    className="wishlist-detail-view__heart-btn"
                    ariaLabel={isPropertyInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
                    ariaPressed={isPropertyInWishlist}
                    callback={() => propertyId && handleRemoveProperty(propertyId)}
                    isDisabled={isRemovingProperty}
                    testId="wishlist-remove-property"
                  >
                    <AIcon
                      icon={clsx(
                        'icon-m icon-inverse',
                        isPropertyInWishlist ? 'icon-heart-fill' : 'icon-heart-outline'
                      )}
                      size={AIconSize.S}
                    />
                  </Button>
                  <PropertyCard
                    titleDetails={titleDetails}
                    brandDetails={brandDetails}
                    images={images}
                    reviewsDetails={reviewsDetails}
                    tertiaryLinkDetails={tertiaryLinkDetails}
                    footerLinkDetails={footerLinkDetails}
                    isVerticalCard={true}
                    addScrimToImage={true}
                    showMobileVariantOnly={true}
                    isMBOP={false}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {totalCount > pageSize && (
        <div className="wishlist-detail-view__pagination">
          <Pagination
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={page => {
              setCurrentPage(page);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </div>
      )}
    </WishListDetailViewStyle>
  );
};
